import { LoaderService } from './../../../shared/services/loader/loader.service';
import { Component, HostListener } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { sessionAuthData, sessionUserData } from 'src/app/shared/interfaces/states-data';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { getSessionAuthData } from 'src/app/shared/states/session-auth/session-auth.selectors';
import { JwtHelperService } from '@auth0/angular-jwt';
import { DateTime, Duration } from "luxon";
import { environment } from '../../../../environments/environment';
import { __clearSessionAuth, __updateSessionAuth } from 'src/app/shared/states/session-auth/session-auth.actions';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { getSessionUserData } from 'src/app/shared/states/session-user/session-user.selectors';
import { __updateSessionUser } from 'src/app/shared/states/session-user/session-user.actions';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent {

  isLoading: Observable<boolean>;
  protected ngUnsubscribe: Subject<void> = new Subject<void>();

  debugActive: boolean = false;

  // Auth States
  sessionAuthState: sessionAuthData = { loggedIn: false };
  sessionExist: boolean = false;

  // User States
  sessionUserState: sessionUserData = {};

  // Json de tokens
  accessTokenData: any = null;
  refreshTokenData: any = null;

  // Intervalos de tiempo
  accessTokenRT: string = '';
  refreshTokenRT: string = '';
  updateSessionRT: string = '';
  accessTokenID: string = ''; // Initial Duration
  refreshTokenID: string = ''; // Initial Duration
  updatingSession: boolean = false;

  constructor(
    private store: Store<any>,
    private authService: AuthService,
    private jwtHelper: JwtHelperService,
    private _router: Router,
    private cookieService: CookieService,
    private _loader: LoaderService
  ) {


    /**
     * Active Loading when resolveFn execute
     */
    this.isLoading = this._loader.isLoading.asObservable();

    console.warn('%c -------> Contructor App', 'color: white');

    // Estado guardado en local storage
    this.sessionAuthState = JSON.parse(localStorage.getItem('sessionAuthState') || JSON.stringify(this.sessionAuthState));
    this.sessionUserState = JSON.parse(localStorage.getItem('sessionUserState') || JSON.stringify(this.sessionUserState));

    // Guardar en reducer el estado del local storage
    this.store.dispatch(__updateSessionAuth(this.sessionAuthState));
    this.store.dispatch(__updateSessionUser(this.sessionUserState));
    console.log(1)
    // Al recibir cambios del reducer - Auth
    this.store.pipe(
      select(getSessionAuthData),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((data: sessionAuthData) => {

      // console.warn(`%c -------> loggedIn: ${data.loggedIn}`, 'color: white');

      // No se esta actualizando la sesion
      this.updatingSession = false;

      // Se guarda en local storage el estado obtenido
      localStorage.setItem('sessionAuthState', JSON.stringify(data));

      // Se guarda en el componente local el estado obtenido
      this.sessionAuthState = data;

      // Se desencriptan los tokens
      this.refreshTokenData = this.sessionAuthState.refresh_token ? this.jwtHelper.decodeToken(this.sessionAuthState.refresh_token || '') : null;
      this.accessTokenData = this.sessionAuthState.access_token ? this.jwtHelper.decodeToken(this.sessionAuthState.access_token || '') : null;

      // Si el token se desencripta correctamente
      if (!this.sessionExist && this.accessTokenData && this.refreshTokenData) {
        // console.warn('%c -------> Session Exist', 'color: white');
        this.sessionExist = true;
      }

      // Se detecta si otro componente dispara el action de __clearSessionAuth
      // ya que en el componente actual this.sessionExist seguiria manteniendo
      // un valor de sesion por lo que se procede a eliminar la sesion local
      if (!this.sessionAuthState.loggedIn && this.sessionExist) {
        console.warn('%c -------> Logout Condition', 'color: white');
        this.clearSession();
      }
    });

    // Al recibir cambios del reducer - User
    this.store.pipe(select(getSessionUserData), takeUntil(this.ngUnsubscribe)).subscribe((data: sessionUserData) => {

      // Se guarda en local storage el estado obtenido
      localStorage.setItem('sessionUserState', JSON.stringify(data));

      // Se guarda en el componente local el estado obtenido
      this.sessionUserState = data;

    });

    // Iniciar timer de sesion
    this.sessionUpdate();

  }

  /* Al presionar escape cerrar el sidebar */
  @HostListener('document:keydown.f9')
  onKeydownHandler() {
    this.debugActive = !this.debugActive;
  }

  /* Comprobar sesion cada 0.3 seg */
  sessionUpdate() {
    setInterval(() => {

      // Comprobar cookie de inicio de sesion
      this.checkLegacyCookie();

      // Comprar tokens de sesion
      this.checkSessionLogic();

    }, 333);
  }

  /* Comprobar el estado de la cookie de goliiive.com */
  checkLegacyCookie() {
    if (this.cookieService.get('logout_session') == 'true') {
      this.clearSession();
    }
    if (this.cookieService.get('access_token') && this.cookieService.get('refresh_token')) {
      const accessToken = this.cookieService.get('access_token');
      const refreshToken = this.cookieService.get('refresh_token');
      this.store.dispatch(__updateSessionAuth({
        ...this.sessionAuthState,
        loggedIn: true,
        access_token: accessToken,
        refresh_token: refreshToken
      }));

      let tokenData = this.jwtHelper.decodeToken(accessToken);

      // Guardar en reducer - User
      this.store.dispatch(__updateSessionUser({
        email: tokenData.user_email,
        user_id: tokenData.user_id,
        user_name: tokenData.user_name,
        user_last_name: tokenData.user_last_name
      }));

      return;
    }

  }

  /* Comprobar estado de los token de sesion */
  checkSessionLogic() {

    // Logica (Access & Refresh Token)
    if (this.accessTokenData?.iat && this.refreshTokenData?.iat) {

      // Duracion del token (Refresh)
      let initialRTD = Duration.fromObject(DateTime.fromSeconds(this.refreshTokenData.exp).diff(DateTime.fromSeconds(this.refreshTokenData.iat)).toObject());

      // Duracion de la cuenta regresiva (Refresh)
      let durationRT = Duration.fromObject(DateTime.fromSeconds(this.refreshTokenData.exp).diff(DateTime.now()).toObject());

      // Duracion del token (Access)
      let initialATD = Duration.fromObject(DateTime.fromSeconds(this.accessTokenData.exp).diff(DateTime.fromSeconds(this.accessTokenData.iat)).toObject());

      // Duracion de la cuenta regresiva (Access)
      let durationAT = Duration.fromObject(DateTime.fromSeconds(this.accessTokenData.exp).diff(DateTime.now()).toObject());

      // Duracion de la cuenta regresiva (Para Actualizar Token) (Access)
      let durationUT = Duration.fromObject(DateTime.fromSeconds(this.accessTokenData.exp).diff(DateTime.now().plus({ seconds: environment.updateSessionAtTime })).toObject());

      // Duracion zero para comparar la condicion
      let zeroDuration = Duration.fromObject({});

      // Impresión Debug
      this.refreshTokenRT = durationRT <= zeroDuration ? '00:00' : durationRT.toFormat('hh:mm:ss');
      this.accessTokenRT = durationAT <= zeroDuration ? '00:00' : durationAT.toFormat('hh:mm:ss');
      this.updateSessionRT = durationUT <= zeroDuration ? '00:00' : durationUT.toFormat('hh:mm:ss');
      this.accessTokenID = initialATD.toFormat('hh:mm:ss');
      this.refreshTokenID = initialRTD.toFormat('hh:mm:ss');

      // TESTING
      // if (durationUT.as('seconds') < 270) {
      //   throw new Error('Session Expired');
      // }

      // Si expira el token de refrescamiento
      if (durationRT <= zeroDuration) {
        console.warn('%c -------> Refresh Token Expired', 'color: white');
        this.clearSession();
        return;
      }

      // Al faltar <environment.updateSessionAtTime> segundos para que la sesión 
      // expire se refresca el token de sesion con el token de refrescamiento
      if ((durationUT <= zeroDuration || durationAT <= zeroDuration) && !this.updatingSession && this.sessionAuthState.refresh_token) {
        console.warn('%c -------> Access Token Close to Expire', 'color: white');
        this.updatingSession = true;
        this.refreshSessionByToken(this.sessionAuthState.refresh_token);
      }

      return;
    }

    // Logica (Refresh Token)
    if (this.refreshTokenData?.iat) {
      // Si solo hay token de refrescamiento se actualiza la sesion
      if (!this.updatingSession && this.sessionAuthState.refresh_token) {
        console.warn('%c -------> Refresh Token Exist Update Session', 'color: white');
        this.updatingSession = true;
        this.refreshSessionByToken(this.sessionAuthState.refresh_token);
      }
    }
  }

  /* Actualizar la sesion con el token de refrescamiento */
  refreshSessionByToken(token: string) {
    console.warn('%c -------> refreshSessionByToken()', 'color: white');
    this.authService.refreshToken({ refresh_token: token })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data) => {
          if (data.statusCode == 200) {
            console.warn('%c -------> refreshSessionByToken() -> Response', 'color: white');
            // Guardar en reducer
            this.store.dispatch(__updateSessionAuth({
              loggedIn: true,
              access_token: data.data[0].access_token,
              refresh_token: data.data[0].refresh_token,
              permissions_hash: data.data[0].permissions_hash,
              data_user_hash: data.data[0].data_user_hash,
              permissions: data.data[0].permissions
            }));
          }
        },
        error: (error) => {
          // console.warn('%c -------> refreshSessionByToken() -> Response Error', 'color: white');
          // console.log(error);
          this.clearSession();
        },
      });
  }

  /* Limpiar la sesion de la aplicacion */
  clearSession() {

    console.warn('%c -------> clearSession()', 'color: white');

    // La sesion no existe
    this.sessionExist = false;

    // Borrar sesion del reducer
    this.store.dispatch(__clearSessionAuth());

    // Navegar al login
    this._router.navigate(['auth']);
  }
}
